import React, { useState, useEffect, useRef } from 'react';
import styled, { keyframes } from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';

import theme from '../constants/theme';
import { getFirstName, isCSR, isMobile } from '../helpers';

import useForm from '../hooks/useForm';

import ContactActions from '../actions/contact';

import Icon from './Icon';
import VendorChatCard from './VendorChatCard';

const animateIn = keyframes`
from {
  transform: translateY(100%);
  opacity: .5;
}
`;

const Wrapper = styled.div`
  animation: ${animateIn} 0.4s 0.75s ease-out;
  animation-fill-mode: backwards;
  background: ${theme.primary600};
  border-radius: 5px 5px 0 0;
  box-shadow: 0 2px 22px rgba(0, 0, 0, 0.25);
  bottom: 0;
  position: fixed;
  right: 20px;
  width: 320px;
  z-index: 999;
`;

const SendButton = styled.div`
  align-items: center;
  color: ${theme.primary600};
  cursor: pointer;
  display: flex;
  justify-content: center;
  width: 40px;

  &:hover {
    opacity: 0.5;
  }
`;

const Title = styled.h2`
  color: #fff;
  cursor: pointer;
  text-transform: uppercase;
  font-size: 14px;
  margin: 0;
  padding: 14px 22px;
  display: flex;
  justify-content: space-between;
`;

const Body = styled.div`
  background: #fff;
  border-left: solid 1px ${theme.grey.lighter};
  border-right: solid 1px ${theme.grey.lighter};
  border-bottom: solid 1px ${theme.grey.lighter};
  max-height: ${p => (p.collapsed ? '0px' : '1000px')};
  overflow: hidden;
  transition: max-height 0.3s ease-in-out;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
`;

const animateInputIn = keyframes`
from{max-height: 0;padding:0 16px;opacity:.2;}
  to{max-height: 30px;}
`;

const Input = styled.input`
  animation: ${animateInputIn} 0.25s ease-in-out;
  border: none;
  border-bottom: 1px solid ${theme.grey.lighter};
  margin: 0;
  padding: 12px 16px;
  font-family: sans-serif;
  font-size: 14px;
  overflow: hidden;

  &:focus {
    outline: none;
  }

  &:last-child {
    border-bottom: none;
  }
`;

const Textarea = styled(Input).attrs({
  as: 'textarea',
})`
  line-height: 1.5;
  min-height: 45px;
  resize: none;
  white-space: break-spaces;
`;

const InvisibleMessageInput = styled(Textarea).attrs({
  as: 'div',
})`
  left: -999px;
  position: fixed;
  top: 0;
`;

const MessageList = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  padding: 20px;
  max-height: 300px;
  overflow-y: scroll;
`;

const MessageInputWrapper = styled.div`
  display: grid;
  grid-template-columns: 10fr 1fr;
  border-top: solid 1px ${theme.grey.lighter};
`;

const Message = styled.div`
  align-self: ${p => (p.recipient ? 'flex-start' : 'flex-end')};
  background: ${p => (p.recipient ? '#ececec' : theme.primary600)};
  border-radius: ${p =>
    p.recipient ? '12px 12px 12px 0' : '12px 12px 0 12px'};
  color: #fff;
  color: ${p => (p.recipient ? '#444' : '#fff')};
  font-size: 14px;
  font-weight: normal;
  margin: ${p => (p.recipient ? '6px 0' : '6px 0 24px')};
  max-width: 80%;
  opacity: ${p => (p.isPending ? '.5' : '1')};
  padding: 8px;
  position: relative;

  &:after {
    content: ${p => (!!p.isPending ? "'Sending...'" : "'You'")};
    color: #ccc;
    width: 100%;
    display: ${p => (p.recipient ? 'none' : 'block')};
    text-align: ${p => (p.recipient ? 'left' : 'right')};
    font-size: 12px;
    position: absolute;
    bottom: -18px;
    left: 0;
    width: 100%;
  }
`;

const PresendInfo = styled.div`
  color: #999;
  background: #f9f9f9;
  border-radius: 3px;
  font-size: 13px;
  max-width: 270px;
  margin: 0 auto 24px;
  padding: 16px;
  text-align: left;

  ul {
    /* list-style-position: inside; */
    /* padding: 0; */

    li {
      margin-bottom: 4px;
      margin-left: -4px;
    }
  }

  em {
    color: ${p => p.theme.primary600};
    display: block;
    font-style: normal;
    font-weight: bold;
    margin-bottom: 6px;

    /* icon */
    div {
      margin-right: 6px;
    }
  }
`;

export default function MessageBox({ userId }) {
  const dispatch = useDispatch();
  const { contact, vendor } = useSelector(s => s);
  const contactDetails = contact.contact_details;
  const isSending = contact.is_sending;
  const previouslyContacted = contact.message_history[vendor.id] || [];
  // const [lastSend, setLastSend] = useState(new Date().toISOString());
  const [hasFinishedMessage, setHasFinishedMessage] = useState(false);
  const [hasErrorMessage, setHasErrorMessage] = useState(false);
  const messageListRef = useRef(null);
  const messageInputRef = useRef(null);
  const invisibleMessageInputRef = useRef(null);
  const [messageInputHeight, setMessageInputHeight] = useState('auto');
  const [invisibleMessageWidth, setInvisibleMessageWidth] = useState('0');
  const collapsed = !contact.showing_message_box;
  const name = getFirstName(vendor.contact);
  const { values, handleChange } = useForm(contactDetails);

  useEffect(() => {
    // Set the width of the invisible message so that and the visible one break in the same spots
    const messageInputWidth = messageInputRef.current.offsetWidth;
    setInvisibleMessageWidth(`${messageInputWidth}px`);
  });

  useEffect(() => {
    messageListRef.current.scrollTop = messageListRef.current.scrollHeight; // Scroll the message box to the bottom
  }, [
    previouslyContacted, // When messages get added to the list, we want to scroll to the bottom of the list
    isSending, // Also recalculate when the user hits send to show the sending message
  ]);

  useEffect(() => {
    if (isCSR && !isMobile) {
      dispatch(ContactActions.setMessageBox(true));
    }
  }, [isCSR]);

  // On changing of the message, recalculate the height of the invisible message input that we use
  // to give a height to the visible input
  useEffect(() => {
    const invisibleInputHeight = invisibleMessageInputRef.current.offsetHeight;
    const newHeight = invisibleInputHeight > 135 ? 135 : invisibleInputHeight;
    setMessageInputHeight(newHeight);
  }, [values['message']]);

  const handleInitialSubmit = ev => {
    if (values['first_name'] && values['phone'] && values['email']) {
      handleFinalSubmit(ev);
    } else {
      setHasFinishedMessage(true);
    }
  };

  const handleFinalSubmit = ev => {
    const vendorId = vendor.id;
    ev.preventDefault();
    if (!hasFinishedMessage) {
      return setHasFinishedMessage(true);
    }
    dispatch(ContactActions.cacheContactFields(values));

    dispatch(
      ContactActions.sendEnquiry({
        vendorId,
        formData: values,
        location: 'message-box',
        onSuccess: () => {
          // setLastSend(new Date().toISOString());
          values['message'] = '';
          setHasFinishedMessage(false);
        },
        onError: () => {
          setHasErrorMessage(true);
          console.error(e);
        },
      })
    );
  };

  return (
    <Wrapper collapsed={collapsed}>
      <Title onClick={() => dispatch(ContactActions.setMessageBox(collapsed))}>
        <span>Message {name}</span>
        <span>{collapsed ? '↑' : '↓'}</span>
      </Title>
      <Body collapsed={collapsed}>
        <VendorChatCard vendor={vendor} />
        <MessageList ref={messageListRef}>
          {!!previouslyContacted &&
            previouslyContacted.map((instance, index) => (
              <Message key={index}>{instance.message}</Message>
            ))}
          {!!previouslyContacted && previouslyContacted.length > 0 && (
            <Message recipient={true}>
              Don't wait around. When {name} replies, it will come to your
              email.
            </Message>
          )}
          {!!hasErrorMessage && (
            <Message recipient={true}>
              There was a problem sending your message. Try sending using the
              contact form at the bottom of the page.
            </Message>
          )}
          {isSending && <Message isPending={true}>{values['message']}</Message>}
        </MessageList>

        {!isSending &&
          hasFinishedMessage &&
          previouslyContacted &&
          previouslyContacted.length < 1 && (
            <PresendInfo>
              <em>
                <Icon size={20} type="clock" color={theme.primary600} />
                Ready to send...
              </em>
              <p>So {name} can reply, enter a couple of details.</p>
              <p>You only need to do this once.</p>
            </PresendInfo>
          )}

        {!isSending &&
          !hasFinishedMessage &&
          previouslyContacted &&
          previouslyContacted.length < 1 && (
            <PresendInfo>
              <strong>Tip - Don't forget to include:</strong>
              <ul>
                <li>Wedding location/venue</li>
                <li>Length of time you requre their services</li>
                <li>Any additional services you need from them</li>
                <li>When you are available for a call</li>
              </ul>
            </PresendInfo>
          )}

        <Form
          onSubmit={ev => {
            handleFinalSubmit(ev);
          }}
        >
          {hasFinishedMessage && (
            <>
              <Input
                required
                onChange={handleChange}
                value={values['first_name']}
                name="first_name"
                placeholder="Your Name"
              />
              <Input
                required
                onChange={handleChange}
                value={values['phone']}
                name="phone"
                placeholder="Phone Number"
              />
              <Input
                required
                onChange={handleChange}
                value={values['email']}
                name="email"
                type="email"
                placeholder="Your Email"
              />
            </>
          )}
          <MessageInputWrapper>
            <Textarea
              required
              onChange={handleChange}
              value={values['message']}
              name="message"
              placeholder="Message"
              ref={messageInputRef}
              style={{
                height: messageInputHeight,
              }}
            />
            {hasFinishedMessage ? (
              <SendButton onClick={ev => handleFinalSubmit(ev)}>
                <Icon size={20} type="send" color={theme.primary600} />
              </SendButton>
            ) : (
              <SendButton onClick={ev => handleInitialSubmit(ev)}>
                {/* <Icon
                  onClick={(ev) => handleInitialSubmit(ev)}
                  size={20}
                  type="send"
                  color={theme.primary600}
                /> */}
                →
              </SendButton>
            )}
          </MessageInputWrapper>
        </Form>
      </Body>
      <InvisibleMessageInput
        ref={invisibleMessageInputRef}
        style={{ width: invisibleMessageWidth }}
      >
        {values['message']}
      </InvisibleMessageInput>
    </Wrapper>
  );
}
